import { useRef, useState, type FormEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PortalError, download, getPortalToken, papi, type PortalDetail, type PortalMe } from './api';
import { fmtDate, fmtMoney, usePortalText } from './text';
import { pArea, pPrimary, pSecondary } from './PortalApp';
import { StatusPill } from './PortalList';

const kb = (n: number) => (n > 1024 * 1024 ? `${(n / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(n / 1024))} KB`);

export default function PortalTicket({ me }: { me: PortalMe }) {
  const { slug = '', number = '' } = useParams();
  const { t, lang } = usePortalText();
  const queryClient = useQueryClient();
  const key = ['portal-ticket', number];
  const q = useQuery({ queryKey: key, queryFn: () => papi<PortalDetail>(`/tickets/${number}`), refetchInterval: 60_000 });
  const [error, setError] = useState('');
  const refresh = (d?: PortalDetail) => {
    if (d) queryClient.setQueryData(key, d); else void queryClient.invalidateQueries({ queryKey: key });
    void queryClient.invalidateQueries({ queryKey: ['portal-tickets'] });
  };

  if (q.isLoading) return <p role="status" className="py-16 text-center text-sm text-[#5B6B73]">{t.loading}</p>;
  if (q.isError || !q.data) return <p role="alert" className="rounded-xl bg-white p-8 text-center text-sm">{(q.error as Error)?.message ?? t.genericError}</p>;
  const { ticket, quote, messages, attachments, delivery } = q.data;
  const fileById = new Map(attachments.map(a => [a.id, a]));

  return (
    <div className="flex flex-col gap-5">
      <div>
        <Link to={`/t/${slug}`} className="text-sm text-[#3D6B8A] hover:underline">{t.back}</Link>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="font-mono text-sm text-[#5B6B73]">{ticket.number} · {t.types[ticket.type]}</span>
          <StatusPill status={ticket.status} holdReason={ticket.holdReason} />
          {delivery && ticket.open && (
            <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${delivery.stage === 'uat' ? 'bg-[#FFF1DC] text-[#7A4300]' : 'bg-[#E8F0F5] text-asx-navy'}`}>{t.delivery[delivery.stage]}</span>
          )}
        </div>
        <h1 className="mt-1.5 text-2xl font-semibold leading-snug">{ticket.subject}</h1>
        <p className="mt-1 text-sm text-[#5B6B73]">
          {t.requester}: {ticket.requesterName} · {t.attendedBy}: {ticket.assigneeName ?? t.noOneYet} · {fmtDate(lang, ticket.createdAt, true)}
        </p>
      </div>
      {error && <p role="alert" className="rounded-lg bg-[#FDECEC] px-3 py-2 text-sm text-[#8F1D1D]">{error}</p>}

      {delivery?.uat && <UatCard number={ticket.number} uat={delivery.uat} onDone={refresh} onError={setError} />}
      {delivery?.production && <p className="rounded-xl border border-[#D7E7C6] bg-[#F4F9EE] px-4 py-3 text-sm text-[#2E5A12]">{t.inProduction(fmtDate(lang, `${delivery.production.date}T12:00:00`), fmtDate(lang, `${delivery.production.warrantyUntil}T12:00:00`))}</p>}
      {quote && <QuoteCard number={ticket.number} quote={quote} approver={me.role === 'approver'} onDone={refresh} onError={setError} />}
      {ticket.status === 'resolved' && <ResolvedCard number={ticket.number} notes={ticket.resolution?.notes ?? ''} onDone={refresh} onError={setError} />}

      <section className="rounded-xl border border-[#E1E7E9] bg-white p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-[#5B6B73]">{t.description}</h2>
        <p className="mt-2 whitespace-pre-line text-[15px] leading-relaxed">{ticket.description}</p>
      </section>

      <section className="flex flex-col gap-3" aria-labelledby="p-conv">
        <h2 id="p-conv" className="text-sm font-semibold uppercase tracking-wide text-[#5B6B73]">{t.conversation}</h2>
        {messages.map(m => (
          <article key={m.id} className={`max-w-[92%] rounded-xl border px-4 py-3 ${m.fromCustomer ? 'self-end border-[#D7E7C6] bg-[#F4F9EE]' : 'self-start border-[#E1E7E9] bg-white'}`}>
            <p className="text-xs text-[#5B6B73]"><strong className="font-semibold text-asx-navy">{m.mine ? t.you : m.fromCustomer ? m.author : `${m.author} · ${t.support}`}</strong> · {fmtDate(lang, m.createdAt, true)}</p>
            <p className="mt-1 whitespace-pre-line text-[15px] leading-relaxed">{m.body}</p>
            {m.attachmentIds.length > 0 && (
              <ul className="mt-2 flex flex-wrap gap-2">
                {m.attachmentIds.map(id => fileById.get(id)).filter(Boolean).map(a => (
                  <li key={a!.id}>
                    <button type="button" className="rounded-md border border-[#CFD8DD] bg-white px-2 py-1 text-xs hover:bg-[#F3F6F7]"
                      onClick={() => void download(`/tickets/${ticket.number}/attachments/${a!.id}`, a!.fileName)}>📎 {a!.fileName} · {kb(a!.size)}</button>
                  </li>
                ))}
              </ul>
            )}
          </article>
        ))}
        {ticket.open ? <ReplyBox number={ticket.number} attachmentsEnabled={me.attachmentsEnabled} onDone={() => refresh()} onError={setError} />
          : <p className="rounded-lg bg-[#EEF1F2] px-4 py-3 text-sm text-[#5B6B73]">{t.closedNote}</p>}
      </section>
    </div>
  );
}

function UatCard({ number, uat, onDone, onError }: {
  number: string; uat: NonNullable<NonNullable<PortalDetail['delivery']>['uat']>; onDone: (d: PortalDetail) => void; onError: (m: string) => void;
}) {
  const { t, lang } = usePortalText();
  const [items, setItems] = useState<{ result: 'accepted' | 'rejected' | null; comment: string }[]>(uat.items.map(() => ({ result: null, comment: '' })));
  const [busy, setBusy] = useState(false);
  const ready = items.every(i => i.result === 'accepted' || (i.result === 'rejected' && i.comment.trim().length >= 5));
  const send = async () => {
    setBusy(true); onError('');
    try {
      onDone(await papi<PortalDetail>(`/tickets/${number}/uat`, { method: 'POST', body: JSON.stringify({ items: items.map(i => ({ result: i.result, comment: i.comment })) }) }));
    } catch (err) { onError(err instanceof PortalError ? err.message : t.genericError); } finally { setBusy(false); }
  };
  const set = (k: number, patch: Partial<(typeof items)[number]>) => setItems(items.map((x, j) => (j === k ? { ...x, ...patch } : x)));
  const tone = { pending: 'bg-[#EEF1F2] text-[#3E4A50]', accepted: 'bg-asx-soft text-[#2E5A12]', rejected: 'bg-[#FDECEC] text-[#8F1D1D]' } as const;

  return (
    <section className={`rounded-xl border p-5 ${uat.canDecide ? 'border-[#FFD8A3] bg-[#FFFBF5]' : 'border-[#E1E7E9] bg-white'}`}>
      <h2 className="text-base font-semibold">{t.uatTitle(uat.round)}</h2>
      {uat.canDecide ? (
        <>
          <p className="mt-1 text-sm text-[#3E4A50]">{t.uatIntro}</p>
          {uat.notes && <p className="mt-2 whitespace-pre-line rounded-lg bg-white px-3 py-2 text-sm">{uat.notes}</p>}
          <ol className="mt-4 flex flex-col gap-4">
            {uat.items.map((it, k) => (
              <li key={k} className="flex flex-col gap-2">
                <p className="text-[15px] font-medium"><span className="mr-1 font-mono text-[#5B6B73]">{k + 1}.</span>{it.criterion}</p>
                <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={it.criterion}>
                  {(['accepted', 'rejected'] as const).map(r => (
                    <button key={r} type="button" role="radio" aria-checked={items[k]!.result === r} onClick={() => set(k, { result: r })}
                      className={`h-10 rounded-lg border px-4 text-sm font-medium ${items[k]!.result === r
                        ? (r === 'accepted' ? 'border-[#5A8F2E] bg-asx-soft text-[#2E5A12]' : 'border-[#C0392B] bg-[#FDECEC] text-[#8F1D1D]')
                        : 'border-[#CFD8DD] bg-white text-asx-navy hover:bg-[#F3F6F7]'}`}>
                      {r === 'accepted' ? `✓ ${t.uatAccept}` : `✕ ${t.uatReject}`}
                    </button>
                  ))}
                </div>
                {items[k]!.result === 'rejected' && (
                  <textarea rows={2} className={pArea} placeholder={t.uatWhat} aria-label={`${t.uatWhat} ${k + 1}`} value={items[k]!.comment} onChange={e => set(k, { comment: e.target.value })} />
                )}
              </li>
            ))}
          </ol>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button type="button" className={pPrimary} disabled={!ready || busy} onClick={() => void send()}>{t.uatSubmit}</button>
            {uat.autoAcceptAt && <span className="text-xs text-[#5B6B73]">{t.uatAutoAccept(fmtDate(lang, uat.autoAcceptAt))}</span>}
          </div>
        </>
      ) : (
        <>
          <p className={`mt-2 inline-flex rounded-lg px-3 py-1.5 text-sm font-semibold ${uat.outcome === 'rejected' ? 'bg-[#FDECEC] text-[#8F1D1D]' : 'bg-asx-soft text-[#2E5A12]'}`}>
            {uat.autoAccepted ? t.uatAutoAccepted : uat.outcome === 'rejected' ? t.uatRejected : t.uatAccepted}
          </p>
          <ul className="mt-3 flex flex-col gap-2 text-sm">
            {uat.items.map((it, k) => (
              <li key={k} className="flex items-start gap-2">
                <span className={`mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-xs font-semibold ${tone[it.result]}`}>{t.uatResult[it.result]}</span>
                <span>{it.criterion}{it.comment && <span className="block text-xs text-[#5B6B73]">“{it.comment}”</span>}</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}

function QuoteCard({ number, quote, approver, onDone, onError }: {
  number: string; quote: NonNullable<PortalDetail['quote']>; approver: boolean; onDone: (d: PortalDetail) => void; onError: (m: string) => void;
}) {
  const { t, lang } = usePortalText();
  const [mode, setMode] = useState<'changes' | 'reject' | null>(null);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const decide = async (decision: 'approve' | 'reject' | 'changes') => {
    setBusy(true); onError('');
    try {
      onDone(await papi<PortalDetail>(`/tickets/${number}/quote`, { method: 'POST', body: JSON.stringify({ decision, reason }) }));
      setMode(null);
    } catch (err) { onError(err instanceof PortalError ? err.message : t.genericError); } finally { setBusy(false); }
  };
  const d = quote.decision;
  return (
    <section className={`rounded-xl border p-5 ${quote.canDecide ? 'border-[#FFD8A3] bg-[#FFFBF5]' : 'border-[#E1E7E9] bg-white'}`}>
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="text-base font-semibold">{t.quoteTitle}{quote.version > 1 ? ` · v${quote.version}` : ''}</h2>
        {quote.validUntil && !d && <span className={`text-xs ${quote.expired ? 'font-semibold text-[#8F1D1D]' : 'text-[#5B6B73]'}`}>{quote.expired ? t.expired : t.validUntil(fmtDate(lang, quote.validUntil))}</span>}
      </div>
      <p className="mt-3 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="text-3xl font-semibold tabular-nums">{t.quoteHours(quote.hours)}</span>
        {quote.price !== null && <span className="text-xl font-semibold tabular-nums text-[#3D6B8A]">{fmtMoney(lang, quote.price, quote.currency)}</span>}
      </p>
      <p className={`mt-2 inline-flex rounded-lg px-3 py-1.5 text-sm font-semibold ${quote.billing === 'bag' ? 'bg-asx-soft text-[#2E5A12]' : 'bg-[#E8F0F5] text-asx-navy'}`}>
        {quote.billing === 'bag' ? t.quoteBag(quote.hours) : t.quoteAdditional}
      </p>
      {quote.lines?.length ? <QuoteTable quote={quote} /> : quote.mgmtHours > 0 && (
        <dl className="mt-3 grid max-w-xs grid-cols-[1fr_auto] gap-x-6 gap-y-1 text-sm tabular-nums">
          <dt className="text-[#5B6B73]">{t.quoteWork}</dt><dd className="text-right">{quote.subtotalHours} h</dd>
          <dt className="text-[#5B6B73]">{t.quoteMgmt(quote.mgmtPct)}</dt><dd className="text-right">{quote.mgmtHours} h</dd>
          <dt className="border-t border-[#E1E7E9] pt-1 font-semibold">{t.quoteTotal}</dt><dd className="border-t border-[#E1E7E9] pt-1 text-right font-semibold">{quote.hours} h</dd>
        </dl>
      )}
      {quote.notes && <p className="mt-3 whitespace-pre-line text-sm leading-relaxed">{quote.notes}</p>}
      {d && (
        <p className={`mt-3 rounded-lg px-3 py-2 text-sm ${d.outcome === 'approved' ? 'bg-asx-soft text-[#2E5A12]' : 'bg-[#EEF1F2] text-[#3E4A50]'}`}>
          {t.decision[d.outcome](d.by.replace(/\s*\(.*\)$/, ''))} · {fmtDate(lang, d.at, true)}{d.reason ? ` — “${d.reason}”` : ''}
        </p>
      )}
      {quote.canDecide && (
        mode ? (
          <div className="mt-4 flex flex-col gap-2">
            <label className="text-sm font-medium">{mode === 'changes' ? t.reasonChanges : t.reasonReject}
              <textarea rows={3} className={`${pArea} mt-1.5`} value={reason} onChange={e => setReason(e.target.value)} autoFocus />
            </label>
            <div className="flex gap-2">
              <button type="button" className={pPrimary} disabled={busy || reason.trim().length < 5} onClick={() => void decide(mode)}>{t.confirmAction}</button>
              <button type="button" className={pSecondary} onClick={() => { setMode(null); setReason(''); }}>{t.cancel}</button>
            </div>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap gap-2">
            <button type="button" className={pPrimary} disabled={busy} onClick={() => void decide('approve')}>{t.approve}</button>
            <button type="button" className={pSecondary} onClick={() => setMode('changes')}>{t.requestChanges}</button>
            <button type="button" className={`${pSecondary} text-[#8F1D1D]`} onClick={() => setMode('reject')}>{t.reject}</button>
          </div>
        )
      )}
      {!quote.canDecide && !approver && !d && !quote.expired && <p className="mt-3 text-xs text-[#5B6B73]">{t.onlyApprover}</p>}
    </section>
  );
}

function ResolvedCard({ number, notes, onDone, onError }: { number: string; notes: string; onDone: (d: PortalDetail) => void; onError: (m: string) => void }) {
  const { t } = usePortalText();
  const [reopening, setReopening] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const act = async (path: 'confirm' | 'reopen') => {
    setBusy(true); onError('');
    try {
      onDone(await papi<PortalDetail>(`/tickets/${number}/${path}`, { method: 'POST', body: JSON.stringify({ reason }) }));
    } catch (err) { onError(err instanceof PortalError ? err.message : t.genericError); } finally { setBusy(false); }
  };
  return (
    <section className="rounded-xl border border-[#CFE6B8] bg-[#F7FBF2] p-5">
      <h2 className="text-base font-semibold">{t.resolvedTitle}</h2>
      {notes && <p className="mt-2 whitespace-pre-line text-sm leading-relaxed">{notes}</p>}
      <p className="mt-3 text-sm font-medium">{t.resolvedBody}</p>
      {reopening ? (
        <div className="mt-2 flex flex-col gap-2">
          <textarea rows={3} className={pArea} placeholder={t.reopenPlaceholder} value={reason} onChange={e => setReason(e.target.value)} autoFocus />
          <div className="flex gap-2">
            <button type="button" className={pPrimary} disabled={busy || reason.trim().length < 5} onClick={() => void act('reopen')}>{t.send}</button>
            <button type="button" className={pSecondary} onClick={() => setReopening(false)}>{t.cancel}</button>
          </div>
        </div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2">
          <button type="button" className={pPrimary} disabled={busy} onClick={() => void act('confirm')}>{t.confirm}</button>
          <button type="button" className={pSecondary} onClick={() => setReopening(true)}>{t.reopen}</button>
        </div>
      )}
    </section>
  );
}

function ReplyBox({ number, attachmentsEnabled, onDone, onError }: { number: string; attachmentsEnabled: boolean; onDone: () => void; onError: (m: string) => void }) {
  const { t } = usePortalText();
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<{ id: string; fileName: string }[]>([]);
  const [busy, setBusy] = useState<'' | 'send' | 'upload'>('');
  const input = useRef<HTMLInputElement>(null);

  const upload = async (file: File) => {
    setBusy('upload'); onError('');
    try {
      const res = await fetch(`/api/portal/tickets/${number}/attachments?${new URLSearchParams({ name: file.name, type: file.type || 'application/octet-stream' })}`, {
        method: 'POST', headers: { Authorization: `Bearer ${getPortalToken() ?? ''}`, 'Content-Type': 'application/octet-stream' }, body: file
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new PortalError(res.status, String(json.code ?? ''), String(json.error ?? t.genericError));
      setFiles(f => [...f, { id: json.id, fileName: json.fileName }]);
    } catch (err) { onError(err instanceof PortalError ? err.message : t.genericError); } finally { setBusy(''); }
  };
  const send = async (e: FormEvent) => {
    e.preventDefault();
    setBusy('send'); onError('');
    try {
      await papi(`/tickets/${number}/messages`, { method: 'POST', body: JSON.stringify({ body, attachmentIds: files.map(f => f.id) }) });
      setBody(''); setFiles([]); onDone();
    } catch (err) { onError(err instanceof PortalError ? err.message : t.genericError); } finally { setBusy(''); }
  };
  return (
    <form onSubmit={send} className="rounded-xl border border-[#E1E7E9] bg-white p-4">
      <label className="sr-only" htmlFor="p-reply">{t.reply}</label>
      <textarea id="p-reply" rows={4} className={pArea} placeholder={t.replyPlaceholder} value={body} onChange={e => setBody(e.target.value)} />
      {files.length > 0 && <ul className="mt-2 flex flex-wrap gap-2 text-xs">{files.map(f => <li key={f.id} className="rounded-md bg-[#EEF3F6] px-2 py-1">📎 {f.fileName}</li>)}</ul>}
      <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
        {attachmentsEnabled ? (
          <>
            <input ref={input} type="file" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ''; }} />
            <button type="button" className={pSecondary} disabled={Boolean(busy)} onClick={() => input.current?.click()}>{busy === 'upload' ? t.uploading : t.attach}</button>
          </>
        ) : <span />}
        <button type="submit" className={pPrimary} disabled={Boolean(busy) || !body.trim()}>{t.send}</button>
      </div>
    </form>
  );
}

/** Veta Change: la cotización por rubro × disciplina que ve el cliente (horas ya redondeadas). */
function QuoteTable({ quote }: { quote: NonNullable<PortalDetail['quote']> }) {
  const { t } = usePortalText();
  const lines = quote.lines ?? [];
  const phases = [...new Set(lines.map(l => l.phaseName))];
  const abap = lines.some(l => l.discipline === 'abap');
  const h = (p: string, d: 'functional' | 'abap') => lines.find(l => l.phaseName === p && l.discipline === d)?.hours ?? 0;
  const sum = (d: 'functional' | 'abap') => lines.filter(l => l.discipline === d).reduce((a, l) => a + l.hours, 0);
  const cell = 'px-3 py-1.5 text-right tabular-nums';
  return (
    <div className="mt-3 overflow-x-auto">
      <table className="w-full min-w-[360px] max-w-xl border-collapse text-sm">
        <thead>
          <tr className="border-b border-[#E1E7E9] text-left text-xs uppercase tracking-wide text-[#5B6B73]">
            <th className="py-1.5 pr-3 font-semibold">{t.quotePhase}</th>
            <th className={`${cell} font-semibold`}>{t.quoteFunctional}</th>
            {abap && <th className={`${cell} font-semibold`}>ABAP</th>}
            <th className={`${cell} font-semibold`}>{t.quoteTotal}</th>
          </tr>
        </thead>
        <tbody>
          {phases.map(p => (
            <tr key={p} className="border-b border-[#EEF1F2]">
              <td className="py-1.5 pr-3">{p}</td>
              <td className={cell}>{h(p, 'functional') || '—'}</td>
              {abap && <td className={cell}>{h(p, 'abap') || '—'}</td>}
              <td className={cell}>{h(p, 'functional') + h(p, 'abap')}</td>
            </tr>
          ))}
          <tr className="border-b border-[#E1E7E9] font-semibold">
            <td className="py-1.5 pr-3">{t.quoteSubtotal}</td>
            <td className={cell}>{sum('functional')}</td>
            {abap && <td className={cell}>{sum('abap')}</td>}
            <td className={cell}>{quote.subtotalHours}</td>
          </tr>
          <tr className="border-b border-[#E1E7E9]">
            <td className="py-1.5 pr-3" colSpan={abap ? 3 : 2}>{t.quoteMgmt(quote.mgmtPct)}</td>
            <td className={cell}>{quote.mgmtHours}</td>
          </tr>
          <tr className="text-base font-semibold">
            <td className="py-2 pr-3" colSpan={abap ? 3 : 2}>{t.quoteTotal}</td>
            <td className={`${cell} py-2`}>{quote.hours} h</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
